/**
 * Bundle Flavor Grid
 * Version: 1.0.0
 *
 * Hybrid of flavor_menu (hero grid with tabs) + bundle quantity controls.
 * Manages grid item quantity controls, quantity meter,
 * price updates, tab switching, and cart interception.
 */

(function() {
  'use strict';

  const STORAGE_PREFIX = 'tinbyrd:bundle:';
  const STORAGE_SCHEMA = 1;
  const LOCAL_TTL_MS = 2 * 60 * 60 * 1000;

  const SelectionStore = {
    key(linkKey) {
      return STORAGE_PREFIX + (linkKey || 'default');
    },
    read(linkKey) {
      const key = this.key(linkKey);
      try {
        const ss = sessionStorage.getItem(key);
        if (ss) {
          const parsed = JSON.parse(ss);
          if (parsed && parsed.v === STORAGE_SCHEMA && parsed.items) return parsed.items;
        }
      } catch (e) {}
      try {
        const ls = localStorage.getItem(key);
        if (ls) {
          const parsed = JSON.parse(ls);
          if (parsed && parsed.v === STORAGE_SCHEMA && parsed.items &&
              typeof parsed.ts === 'number' && Date.now() - parsed.ts < LOCAL_TTL_MS) {
            return parsed.items;
          }
          localStorage.removeItem(key);
        }
      } catch (e) {}
      return null;
    },
    write(linkKey, items) {
      const key = this.key(linkKey);
      const payload = JSON.stringify({ v: STORAGE_SCHEMA, ts: Date.now(), items: items });
      try { sessionStorage.setItem(key, payload); } catch (e) {}
      try { localStorage.setItem(key, payload); } catch (e) {}
    },
    clear(linkKey) {
      const key = this.key(linkKey);
      try { sessionStorage.removeItem(key); } catch (e) {}
      try { localStorage.removeItem(key); } catch (e) {}
    }
  };

  class BundleFlavorGrid {
    constructor(container) {
      this.container = container;
      this.items = container.querySelectorAll('.bundle-flavor-grid__item');
      this.quantityInputs = container.querySelectorAll('.bundle-flavor-grid__qty-input');
      this.quantityMeter = this.resolveQuantityMeter();
      this.storageKey = container.dataset.quantityMeterLinkKey || null;
      this._persistTimer = null;

      this.selectedVariants = new Map();

      // Trouver le formulaire produit principal (pas dans un drawer)
      let formCandidate = container.closest('form[action*="/cart/add"]');

      if (!formCandidate) {
        formCandidate = container.parentElement?.querySelector('form[action*="/cart/add"]');
      }

      if (!formCandidate) {
        const allForms = document.querySelectorAll('form[action*="/cart/add"]');
        for (const form of allForms) {
          const isInDrawer = form.closest('cart-drawer') ||
                             form.closest('#CartDrawer') ||
                             form.classList.contains('upsell-drawer-form');
          if (!isInDrawer) {
            formCandidate = form;
            break;
          }
        }
      }

      if (formCandidate && (
          formCandidate.closest('cart-drawer') ||
          formCandidate.closest('#CartDrawer') ||
          formCandidate.classList.contains('upsell-drawer-form')
      )) {
        this.productForm = null;
      } else {
        this.productForm = formCandidate;
      }

      this.tiers = [
        { qty: 1, text: '1', price: '' },
        { qty: 2, text: '2', price: '' },
        { qty: 3, text: '3', price: '' },
        { qty: 4, text: '4', price: '' }
      ];

      // 'quantity' = thresholds counted in number of bags (legacy).
      // 'amount'   = thresholds counted in cart amount (cents).
      this.meterMode = container.dataset.meterMode === 'amount' ? 'amount' : 'quantity';
      this.moneySymbol = container.dataset.moneySymbol || '€';
      this.moneyDecimal = container.dataset.moneyDecimal || ',';

      this.discounts = {
        '1': { type: 'none', value: 0 },
        '2': { type: 'none', value: 0 },
        '3': { type: 'none', value: 0 },
        '4': { type: 'none', value: 0 }
      };

      this.init();
    }

    resolveQuantityMeter() {
      const linkKey = this.container.dataset.quantityMeterLinkKey;

      if (linkKey) {
        const quantityMeters = document.querySelectorAll('[data-quantity-meter-addon]');
        for (const meter of quantityMeters) {
          if (meter.dataset.quantityMeterLinkKey === linkKey) {
            return meter;
          }
        }
      }

      return this.container.querySelector('[data-quantity-meter-addon]') || document.querySelector('[data-quantity-meter-addon]');
    }

    init() {
      try {
        this.loadConfiguration();
        this.bindTabs();
        this.bindQuantityControls();
        this.rehydrateSelections();
        this.initializeQuantityButtons();
        this.updateTabCounts();
        this.updateQuantityMeter();
        this.updateButtonPrice();
        this.updateSubmitButtonState();
        this.interceptFetchRequests();
        document.addEventListener('buttonPriceManagerReady', () => {
          if (this.selectedVariants.size > 0) {
            this.updateButtonPrice();
          }
        });
      } catch (e) {
        console.error('[BundleFlavorGrid] Init error:', e);
      }
    }


    // ══════════════════════════════════════════
    // Configuration
    // ══════════════════════════════════════════

    loadConfiguration() {
      const config = this.container.dataset;

      if (config.tiers) {
        try {
          this.tiers = JSON.parse(config.tiers);
        } catch (e) {
          // Use defaults
        }
      }

      if (config.discounts) {
        try {
          this.discounts = JSON.parse(config.discounts);
        } catch (e) {
          // Use defaults
        }
      }
    }

    // ══════════════════════════════════════════
    // Tabs
    // ══════════════════════════════════════════

    bindTabs() {
      const tabs = this.container.querySelectorAll('.flavor-menu__tab');
      const panels = this.container.querySelectorAll('.flavor-menu__panel');
      const subtitles = this.container.querySelectorAll('.flavor-menu__subtitle');

      tabs.forEach(tab => {
        tab.addEventListener('click', () => {
          const targetPanel = tab.dataset.tab;

          tabs.forEach(t => {
            t.classList.remove('is-active');
            t.setAttribute('aria-selected', 'false');
          });
          tab.classList.add('is-active');
          tab.setAttribute('aria-selected', 'true');

          panels.forEach(panel => {
            if (panel.dataset.tabPanel === targetPanel) {
              panel.removeAttribute('hidden');
              panel.classList.add('is-active');
            } else {
              panel.setAttribute('hidden', '');
              panel.classList.remove('is-active');
            }
          });

          subtitles.forEach(sub => {
            if (sub.dataset.tabSub === targetPanel) {
              sub.removeAttribute('hidden');
              sub.classList.add('is-active');
            } else {
              sub.setAttribute('hidden', '');
              sub.classList.remove('is-active');
            }
          });
        });
      });
    }

    // ══════════════════════════════════════════
    // Quantity Controls
    // ══════════════════════════════════════════

    bindQuantityControls() {
      this.quantityInputs.forEach(input => {
        const quantityContainer = input.closest('.bundle-flavor-grid__quantity-controls');
        if (!quantityContainer) return;

        const plusButton = quantityContainer.querySelector('[name="plus"]');
        const minusButton = quantityContainer.querySelector('[name="minus"]');

        if (plusButton) {
          plusButton.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.incrementQuantity(input);
          });
        }

        if (minusButton) {
          minusButton.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.decrementQuantity(input);
          });
        }
      });
    }

    incrementQuantity(input) {
      const currentValue = parseInt(input.value) || 0;
      const max = parseInt(input.dataset.max) || 999;

      if (currentValue < max) {
        const newValue = currentValue + 1;
        input.value = newValue;
        this.handleQuantityChange(input, newValue);
      }
    }

    decrementQuantity(input) {
      const currentValue = parseInt(input.value) || 0;

      if (currentValue > 0) {
        const newValue = currentValue - 1;
        input.value = newValue;
        this.handleQuantityChange(input, newValue);
      }
    }

    handleQuantityChange(input, quantity) {
      const variantId = input.dataset.variantId;
      const item = input.closest('.bundle-flavor-grid__item');
      const max = parseInt(input.dataset.max) || 999;

      if (quantity < 0) {
        input.value = 0;
        quantity = 0;
      }

      if (quantity > max) {
        input.value = max;
        quantity = max;
      }

      if (quantity > 0) {
        const compareAtPrice = item.dataset.variantCompareAtPrice;
        this.selectedVariants.set(variantId, {
          quantity: quantity,
          price: parseInt(item.dataset.variantPrice),
          compare_at_price: compareAtPrice ? parseInt(compareAtPrice) : null,
          title: item.querySelector('.flavor-menu__item-title')?.textContent.trim() || ''
        });
      } else {
        this.selectedVariants.delete(variantId);
      }

      this.updateQuantityButtons(input);
      this.updateTabCounts();
      this.updateQuantityMeter();
      this.updateButtonPrice();
      this.updateSubmitButtonState();
      this.schedulePersist();
    }

    rehydrateSelections() {
      const stored = SelectionStore.read(this.storageKey);
      if (!stored) return;

      const validVariantIds = new Set();
      this.quantityInputs.forEach(input => validVariantIds.add(input.dataset.variantId));

      let applied = 0;
      this.quantityInputs.forEach(input => {
        const variantId = input.dataset.variantId;
        const savedQty = stored[variantId];
        if (!savedQty || !validVariantIds.has(variantId)) return;

        const max = parseInt(input.dataset.max) || 999;
        const qty = Math.min(parseInt(savedQty) || 0, max);
        if (qty <= 0) return;

        const item = input.closest('.bundle-flavor-grid__item');
        if (!item) return;

        input.value = qty;
        const compareAtPrice = item.dataset.variantCompareAtPrice;
        this.selectedVariants.set(variantId, {
          quantity: qty,
          price: parseInt(item.dataset.variantPrice),
          compare_at_price: compareAtPrice ? parseInt(compareAtPrice) : null,
          title: item.querySelector('.flavor-menu__item-title')?.textContent.trim() || ''
        });
        applied++;
      });

      if (applied > 0) {
        this.schedulePersist();
      }
    }

    schedulePersist() {
      if (!this.storageKey) return;
      if (this._persistTimer) clearTimeout(this._persistTimer);
      this._persistTimer = setTimeout(() => {
        this._persistTimer = null;
        if (this.selectedVariants.size === 0) {
          SelectionStore.clear(this.storageKey);
          return;
        }
        const items = {};
        this.selectedVariants.forEach((data, variantId) => {
          items[variantId] = data.quantity;
        });
        SelectionStore.write(this.storageKey, items);
      }, 150);
    }

    updateTabCounts() {
      const panels = this.container.querySelectorAll('.flavor-menu__panel');
      panels.forEach(panel => {
        const tabKey = panel.dataset.tabPanel;
        let total = 0;
        panel.querySelectorAll('.bundle-flavor-grid__qty-input').forEach(input => {
          total += parseInt(input.value) || 0;
        });
        const badge = this.container.querySelector(`[data-tab-count="${tabKey}"]`);
        if (!badge) return;
        if (total > 0) {
          badge.textContent = total;
          badge.hidden = false;
        } else {
          badge.hidden = true;
        }
      });
    }

    updateQuantityButtons(input) {
      const quantityContainer = input.closest('.bundle-flavor-grid__quantity-controls');
      if (!quantityContainer) return;

      const plusButton = quantityContainer.querySelector('[name="plus"]');
      const minusButton = quantityContainer.querySelector('[name="minus"]');

      const value = parseInt(input.value) || 0;
      const max = parseInt(input.dataset.max) || 999;

      if (minusButton) {
        minusButton.disabled = value <= 0;
        minusButton.classList.toggle('disabled', value <= 0);
      }
      if (plusButton) {
        plusButton.disabled = value >= max;
        plusButton.classList.toggle('disabled', value >= max);
      }
    }

    initializeQuantityButtons() {
      this.quantityInputs.forEach(input => {
        this.updateQuantityButtons(input);
      });
    }

    // ══════════════════════════════════════════
    // Meter helpers (quantity vs amount mode)
    // ══════════════════════════════════════════

    getTotalQuantity() {
      let total = 0;
      this.selectedVariants.forEach(data => { total += data.quantity; });
      return total;
    }

    getTotalAmount() {
      let total = 0;
      this.selectedVariants.forEach(data => { total += data.price * data.quantity; });
      return total;
    }

    // The value compared against tier thresholds: bag count (quantity) or cart amount in cents (amount).
    getMeterValue() {
      return this.meterMode === 'amount' ? this.getTotalAmount() : this.getTotalQuantity();
    }

    // Each tier's threshold in the unit matching meterMode.
    // amount mode: tier.qty is configured in euros (Liquid emits it ×100 → cents).
    getTierThreshold(tier) {
      const raw = (tier && typeof tier.threshold !== 'undefined') ? tier.threshold : (tier ? tier.qty : 0);
      return parseFloat(raw) || 0;
    }

    formatMoney(cents) {
      // Montant rond → pas de décimales ("20€") ; sinon 2 décimales ("26,10€").
      const value = (cents % 100 === 0)
        ? String(cents / 100)
        : (cents / 100).toFixed(2).replace('.', this.moneyDecimal);
      return value + this.moneySymbol;
    }

    // ══════════════════════════════════════════
    // Quantity Meter
    // ══════════════════════════════════════════

    updateQuantityMeter() {
      if (!this.quantityMeter) {
        this.quantityMeter = this.resolveQuantityMeter();
      }

      if (!this.quantityMeter) return;

      const meterValue = this.getMeterValue();

      const items = this.quantityMeter.querySelectorAll('.quantity-meter-addon-item');

      let lastActiveTier = -1;
      for (let i = 0; i < this.tiers.length; i++) {
        if (meterValue >= this.getTierThreshold(this.tiers[i])) lastActiveTier = i;
      }

      for (let i = 0; i < this.tiers.length; i++) {
        const item = items[i];
        if (!item) continue;
        const fill = item.querySelector('[data-bar-fill]');
        const tierThreshold = this.getTierThreshold(this.tiers[i]);
        const prevThreshold = i === 0 ? 0 : this.getTierThreshold(this.tiers[i - 1]);

        let pct;
        if (meterValue >= tierThreshold) {
          pct = 100;
        } else if (meterValue <= prevThreshold) {
          pct = 0;
        } else {
          pct = ((meterValue - prevThreshold) / (tierThreshold - prevThreshold)) * 100;
        }

        const isCurrent = i === lastActiveTier + 1 && pct > 0;
        item.classList.toggle('is-active', i === lastActiveTier);
        item.classList.toggle('is-unlocked', i <= lastActiveTier);
        item.classList.toggle('is-current', isCurrent);
        if (fill) fill.style.width = pct + '%';
        const currentEl = item.querySelector('[data-qm-current]');
        if (currentEl) {
          if (!isCurrent) {
            currentEl.textContent = '';
          } else if (this.meterMode === 'amount') {
            currentEl.textContent = this.formatMoney(meterValue) + '/';
          } else {
            currentEl.textContent = meterValue + '/';
          }
        }
      }

      this.updateIntro(meterValue);
      this.updateGifts(this.getTotalQuantity());
    }

    // Amount mode only: "Plus que XX€ pour bénéficier de -XX%".
    // Falls back to the configured intro text when the cart is empty or all tiers are unlocked.
    updateIntro(meterValue) {
      if (!this.quantityMeter) return;
      const introEl = this.quantityMeter.querySelector('[data-qm-intro]');
      if (!introEl) return;

      const baseText = introEl.dataset.qmIntroDefault || introEl.textContent;

      if (this.meterMode !== 'amount' || meterValue <= 0) {
        introEl.textContent = baseText;
        return;
      }

      let nextTier = null;
      for (let i = 0; i < this.tiers.length; i++) {
        if (meterValue < this.getTierThreshold(this.tiers[i])) {
          nextTier = i;
          break;
        }
      }

      if (nextTier === null) {
        // All tiers unlocked: congratulate with the highest discount reached.
        const lastDiscount = this.discounts[String(this.tiers.length)];
        const lastPct = lastDiscount && lastDiscount.value ? lastDiscount.value : 0;
        if (lastPct > 0) {
          introEl.textContent = 'Bravo ! Vous économisez -' + lastPct + '% sur votre commande.';
        } else {
          introEl.textContent = baseText;
        }
        return;
      }

      const remaining = this.getTierThreshold(this.tiers[nextTier]) - meterValue;
      const discount = this.discounts[String(nextTier + 1)];
      const pct = discount && discount.value ? discount.value : 0;

      if (pct <= 0) {
        introEl.textContent = baseText;
        return;
      }

      introEl.textContent = 'Plus que ' + this.formatMoney(remaining) + ' pour bénéficier de -' + pct + '%';
    }

    updateGifts(totalQuantity) {
      const event = new CustomEvent('bundlePackQuantityChanged', {
        detail: {
          totalQuantity: totalQuantity,
          selectedVariants: Array.from(this.selectedVariants.entries())
        }
      });
      document.dispatchEvent(event);
    }

    // ══════════════════════════════════════════
    // Price & Submit Button
    // ══════════════════════════════════════════

    updateButtonPrice() {
      if (!this.selectedVariants) return;

      let totalQuantity = this.getTotalQuantity();
      let totalPriceBeforeDiscount = this.getTotalAmount();

      // Tier unlocked by meter value (bag count or cart amount, matching the gauge).
      const meterValue = this.meterMode === 'amount' ? totalPriceBeforeDiscount : totalQuantity;
      let activeTier = null;
      for (let i = this.tiers.length - 1; i >= 0; i--) {
        if (meterValue >= this.getTierThreshold(this.tiers[i])) {
          activeTier = String(i + 1);
          break;
        }
      }

      let totalCompareAtPrice = 0;
      this.selectedVariants.forEach((data) => {
        const comparePrice = data.compare_at_price || data.price;
        totalCompareAtPrice += (comparePrice * data.quantity);
      });

      let totalPrice = totalPriceBeforeDiscount;
      const discount = this.discounts[activeTier];

      if (discount && discount.type !== 'none') {
        if (discount.type === 'percentage') {
          const reductionMultiplier = (1 - discount.value / 100);
          let discountedTotal = 0;
          this.selectedVariants.forEach((data) => {
            const unitDiscounted = Math.ceil(data.price * reductionMultiplier);
            discountedTotal += unitDiscounted * data.quantity;
          });
          totalPrice = discountedTotal;
        } else if (discount.type === 'fixed') {
          const discountInCentimes = discount.value * 100;
          totalPrice = totalPriceBeforeDiscount - discountInCentimes;
        }
      }

      totalPrice = Math.round(totalPrice);

      const event = new CustomEvent('bundlePackPrice:change', {
        detail: {
          totalPrice: totalPrice,
          totalPriceBeforeDiscount: totalPriceBeforeDiscount,
          totalCompareAtPrice: totalCompareAtPrice,
          selectedVariants: Array.from(this.selectedVariants.entries()),
          hasSelections: this.selectedVariants.size > 0,
          activeTier: activeTier,
          totalQuantity: totalQuantity,
          discount: discount,
          globalDiscountApplied: false
        }
      });
      document.dispatchEvent(event);
    }

    updateSubmitButtonState() {
      const submitButton = document.querySelector('.product-form__submit');
      if (!submitButton) return;

      const textEl = submitButton.querySelector('.product-form__submit-text');
      const separatorEl = submitButton.querySelector('.product-form__submit-separator');
      const priceEl = submitButton.querySelector('.product-form__submit-price');
      if (!textEl) return;

      const hasSelections = this.selectedVariants.size > 0;

      if (!hasSelections) {
        submitButton.disabled = true;
        submitButton.style.opacity = '0.5';
        submitButton.style.cursor = 'not-allowed';
        textEl.textContent = 'Sélectionnez un produit';
        if (separatorEl) separatorEl.style.display = 'none';
        if (priceEl) priceEl.style.display = 'none';
      } else {
        submitButton.disabled = false;
        submitButton.style.opacity = '';
        submitButton.style.cursor = '';
        textEl.textContent = 'Ajouter au panier';
        if (separatorEl) separatorEl.style.display = '';
        if (priceEl) priceEl.style.display = '';
      }
    }

    // ══════════════════════════════════════════
    // Cart Interception
    // ══════════════════════════════════════════

    interceptFetchRequests() {
      const originalFetch = window.fetch;
      const self = this;

      window.fetch = function(...args) {
        const [url, options] = args;

        if (url && (url.includes('/cart/add') || url.includes('cart_add_url'))) {
          let isFromMainForm = false;

          if (options && options.body instanceof FormData) {
            const formData = options.body;
            const formSectionId = formData.get('section-id');
            const mainFormSectionId = self.productForm ? self.productForm.querySelector('input[name="section-id"]')?.value : null;

            if (mainFormSectionId && formSectionId === mainFormSectionId) {
              isFromMainForm = true;
            }
          }

          const shouldIntercept = isFromMainForm && self.selectedVariants.size > 0;

          if (shouldIntercept) {
            let sellingPlanId = null;
            let formProperties = {};
            if (options && options.body instanceof FormData) {
              sellingPlanId = options.body.get('selling_plan');
              for (const [key, value] of options.body.entries()) {
                const match = key.match(/^properties\[(.+)\]$/);
                if (match && value) {
                  formProperties[match[1]] = value;
                }
              }
            }

            const items = [];
            self.selectedVariants.forEach((data, variantId) => {
              const item = {
                id: parseInt(variantId),
                quantity: data.quantity
              };
              if (sellingPlanId) {
                item.selling_plan = parseInt(sellingPlanId);
              }
              if (Object.keys(formProperties).length > 0) {
                item.properties = formProperties;
              }
              items.push(item);
            });

            // Ajouter le bocal upsell-gift si sélectionné
            if (window.__upsellGift) {
              const giftItem = window.__upsellGift.getSelectedItem();
              if (giftItem) {
                items.push({ id: giftItem.id, quantity: giftItem.quantity });
              }
            }

            const jsonBody = { items: items };

            if (options && options.body instanceof FormData) {
              const formData = options.body;
              const sections = formData.get('sections');
              const sections_url = formData.get('sections_url');
              if (sections) jsonBody.sections = sections;
              if (sections_url) jsonBody.sections_url = sections_url;
            }

            options.body = JSON.stringify(jsonBody);

            if (!options.headers) options.headers = {};
            if (options.headers instanceof Headers) {
              options.headers.set('Content-Type', 'application/json');
              options.headers.set('Accept', 'application/json');
            } else {
              options.headers['Content-Type'] = 'application/json';
              options.headers['Accept'] = 'application/json';
            }
          }

          const fetchPromise = originalFetch.apply(this, args);

          if (isFromMainForm && self.selectedVariants.size > 0) {
            return fetchPromise.then(response => {
              if (response.ok) {
                setTimeout(() => {
                  self.reset();
                }, 800);
              }
              return response;
            }).catch(error => {
              console.error('[BundleFlavorGrid] Erreur:', error);
              throw error;
            });
          }

          return fetchPromise;
        }

        return originalFetch.apply(this, args);
      };
    }

    // ══════════════════════════════════════════
    // Reset
    // ══════════════════════════════════════════

    reset() {
      this.selectedVariants.clear();

      this.quantityInputs.forEach(input => {
        input.value = 0;
        this.updateQuantityButtons(input);
      });

      if (this._persistTimer) {
        clearTimeout(this._persistTimer);
        this._persistTimer = null;
      }
      SelectionStore.clear(this.storageKey);

      this.updateTabCounts();
      this.updateQuantityMeter();
      this.updateButtonPrice();
      this.updateSubmitButtonState();
    }
  }

  // Auto-initialize
  function initializeBundleFlavorGrid() {
    const containers = document.querySelectorAll('[data-bundle-flavor-grid]');
    containers.forEach(container => {
      if (!container.bundleFlavorGrid) {
        container.bundleFlavorGrid = new BundleFlavorGrid(container);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeBundleFlavorGrid);
  } else {
    initializeBundleFlavorGrid();
  }

  document.addEventListener('shopify:section:load', initializeBundleFlavorGrid);

  window.BundleFlavorGrid = BundleFlavorGrid;

})();
